import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate, fmtEuro } from "@/lib/format";
import { ArrowLeft, CheckCircle2, Eraser, Loader2, PenLine } from "lucide-react";

export default function MandatPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [signataire, setSignataire] = useState("");
  const [consentMandat, setConsentMandat] = useState(false);
  const [consentProprio, setConsentProprio] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    api
      .get(`/estimations/${id}`)
      .then((r) => {
        setDoc(r.data);
        setSignataire(`${r.data.lead.prenom} ${r.data.lead.nom}`);
        if (r.data.mandat) setSigned(r.data.mandat);
      })
      .catch(() => setError("Cette estimation est introuvable."));
  }, [id]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || signed) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [doc, signed]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const startDraw = (e) => {
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const endDraw = () => {
    drawing.current = false;
  };
  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const submit = async () => {
    if (signataire.trim().length < 3) return toast.error("Indiquez le nom complet du signataire.");
    if (!hasDrawn) return toast.error("Signez dans le cadre prévu à cet effet.");
    if (!consentMandat || !consentProprio) return toast.error("Les deux engagements sont obligatoires.");
    setSubmitting(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const res = await api.post(`/estimations/${id}/mandat`, {
        signataire: signataire.trim(),
        signature,
        consent_mandat: true,
        consent_proprietaire: true,
      });
      setSigned({
        signataire: signataire.trim(),
        signed_at: res.data.signed_at,
        prix_mise_en_vente: res.data.prix_mise_en_vente,
      });
      toast.success("Mandat signé — un exemplaire vous a été envoyé par email.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur lors de la signature.");
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <header className="border-b border-[#D4AF37]/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="mandat-brand-link" className="font-display text-xl text-[#F5F3EF]">
          Valeur <span className="italic text-[#D4AF37]">Prestige</span>
        </Link>
        <Link to={`/rapport/${id}`} data-testid="mandat-back-link" className="btn-ghost !px-4 !py-2 !text-xs">
          <ArrowLeft size={14} /> Retour au rapport
        </Link>
      </div>
    </header>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
        {header}
        <div className="flex flex-col items-center px-6 py-32 text-center">
          <p data-testid="mandat-error" className="font-display text-2xl text-amber-100">{error}</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0B0D]">
        <Loader2 data-testid="mandat-loading" className="animate-spin text-[#D4AF37]" size={28} />
      </div>
    );
  }

  const { property, result } = doc;
  const typeLabel = property.type_bien === "maison" ? "Maison" : "Appartement";

  return (
    <div data-testid="mandat-page" className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
      {header}
      <main className="mx-auto max-w-5xl px-6 py-14">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">Mandat de vente — signature en ligne</p>
        <h1 className="font-display mt-4 text-3xl font-light text-amber-100 sm:text-4xl">
          Passons à la vente, <em className="text-[#D4AF37]">ensemble.</em>
        </h1>

        {signed ? (
          <div data-testid="mandat-signed" className="mt-12 border border-emerald-500/30 bg-emerald-500/5 p-10 text-center">
            <CheckCircle2 size={28} className="mx-auto text-emerald-400" />
            <p className="font-display mt-4 text-2xl text-emerald-300">Mandat de vente signé</p>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-stone-400">
              Signé électroniquement par <span className="text-stone-200">{signed.signataire}</span> le{" "}
              {fmtDate(signed.signed_at)} — prix de mise en vente {fmtEuro(signed.prix_mise_en_vente)}.
              Un exemplaire vous a été envoyé par email. Barsamia vous recontacte sous 24 h ouvrées
              pour lancer la mise en vente de votre bien.
            </p>
            <Link to="/" data-testid="mandat-signed-home" className="btn-ghost mt-8 !text-xs">
              Retour à l'accueil
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="border border-stone-800 bg-[#141417] p-8">
              <h2 className="font-display text-xl text-amber-200/90">Les conditions du mandat</h2>
              <dl className="mt-6 space-y-4 text-sm">
                <div className="flex justify-between gap-4 border-b border-stone-800/60 pb-3">
                  <dt className="text-stone-500">Bien</dt>
                  <dd data-testid="mandat-recap-bien" className="text-right text-stone-200">
                    {typeLabel} · {property.surface} m² · {property.pieces} p.
                  </dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-stone-800/60 pb-3">
                  <dt className="text-stone-500">Adresse</dt>
                  <dd data-testid="mandat-recap-adresse" className="text-right text-stone-200">
                    {property.rue}, {property.code_postal} {property.ville}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-stone-800/60 pb-3">
                  <dt className="text-stone-500">Prix de mise en vente conseillé</dt>
                  <dd data-testid="mandat-recap-prix" className="text-right text-[#E5C158]">{fmtEuro(result.median)}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-stone-800/60 pb-3">
                  <dt className="text-stone-500">Honoraires</dt>
                  <dd className="text-right text-stone-200">5 % TTC à la charge du vendeur</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Durée</dt>
                  <dd className="text-right text-stone-200">3 mois</dd>
                </div>
              </dl>
              <p className="mt-6 text-xs leading-relaxed text-stone-500">
                En signant, vous mandatez Barsamia Tumba (carte professionnelle de transaction
                n° CPI 75012015000002592 — Préfecture de Paris) pour la vente du bien ci-dessus.
                Signature électronique simple au sens du règlement eIDAS ; un exemplaire signé
                vous est envoyé par email. Le mandat définitif vous sera remis pour paraphe
                lors du premier rendez-vous.
              </p>
            </div>

            <div className="border border-[#D4AF37]/25 bg-[#141417] p-8">
              <h2 className="font-display text-xl text-amber-200/90">Votre signature</h2>
              <div className="mt-6">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">
                  Nom complet du signataire
                </label>
                <input
                  data-testid="mandat-signataire-input"
                  className="input-dark"
                  value={signataire}
                  onChange={(e) => setSignataire(e.target.value)}
                />
              </div>
              <div className="mt-6">
                <div className="mb-2 flex items-center justify-between">
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">
                    Signez dans le cadre
                  </label>
                  <button
                    type="button"
                    data-testid="mandat-clear-signature"
                    onClick={clearCanvas}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 transition-colors hover:text-[#E5C158]"
                  >
                    <Eraser size={12} /> Effacer
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  data-testid="mandat-signature-canvas"
                  className="h-44 w-full cursor-crosshair border border-dashed border-stone-700 bg-[#0B0B0D] touch-none"
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                />
              </div>
              <div className="mt-6 space-y-4">
                <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-300" data-testid="mandat-consent-mandat-label">
                  <input
                    type="checkbox"
                    data-testid="checkbox-consent-mandat"
                    checked={consentMandat}
                    onChange={(e) => setConsentMandat(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
                  />
                  <span>J'ai lu et j'accepte les conditions du mandat de vente ci-contre. <span className="text-[#D4AF37]">*</span></span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-300" data-testid="mandat-consent-proprio-label">
                  <input
                    type="checkbox"
                    data-testid="checkbox-consent-proprio"
                    checked={consentProprio}
                    onChange={(e) => setConsentProprio(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
                  />
                  <span>Je certifie être propriétaire du bien (ou dûment mandaté par le propriétaire). <span className="text-[#D4AF37]">*</span></span>
                </label>
              </div>
              <button
                type="button"
                data-testid="mandat-submit-button"
                onClick={submit}
                disabled={submitting}
                className="btn-gold mt-8 w-full justify-center disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Signature en cours…
                  </>
                ) : (
                  <>
                    <PenLine size={16} /> Signer mon mandat
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
